import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "lib/utils";
import {
  Check,
  X,
  Pencil,
  Trash2,
  GripVertical,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { useSwipeActions } from "../../hooks/useSwipeActions";
import {
  builtName,
  formatSessionDate,
  slotName,
} from "../../utils/programmeSchedule";
import { useProgrammeMatesContext } from "../../contexts/ProgrammeMatesContext";
import { mateName, matesBuilds, matesWhoDid } from "../../hooks/useProgrammeMates";

// One session slot in the programme timeline (#59):
//  - tap → the session sheet (build, start, edit, or what you did)
//  - ⋯ menu (all pointers) or swipe (touch) → edit exercises / clear, on a
//    built session that hasn't been done yet
//  - drag handle → reorder within the week, on sessions still to do only;
//    reordering what's already happened means nothing
//
// The slot's planned name ("Upper · Strength") stays the title once it's
// built and done, with the session's own name underneath, so a finished week
// still shows how its structure was followed.
const TimelineSessionItem = ({
  session,
  isNext = false,
  onClickSession,
  onEditSession,
  onClearSession,
}) => {
  const {
    status,
    plan_data,
    completed_at,
    duration_minutes,
    session_notes,
    session_rating,
  } = session;
  const isAssigned = plan_data != null;
  const isPending = status !== "completed" && status !== "missed" && status !== "skipped";
  // Only a filled-but-not-done slot can be edited or cleared.
  const editable = isAssigned && isPending;
  const title = slotName(session);
  const name = builtName(session);
  // Training together (#184): who else did it, and whose build this is.
  const matesCtx = useProgrammeMatesContext();
  const group = matesCtx?.group;
  const didIt = group ? matesWhoDid(group, session).map((m) => m.name.split(" ")[0]) : [];
  const builder =
    group && isAssigned && session.built_by && Number(session.built_by) !== Number(matesCtx.me)
      ? mateName(group, session.built_by)
      : null;
  const offered = group && !isAssigned && isPending ? matesBuilds(group, session) : [];

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: session.id, disabled: !isPending });

  const { offset, dragging, handlers, maxReveal } = useSwipeActions({
    onSwipeRight: editable ? () => onEditSession?.(session) : undefined,
    onSwipeLeft: editable ? () => onClearSession?.(session) : undefined,
  });

  // dnd owns the transform while a reorder is in flight; the swipe owns it the
  // rest of the time. Whichever is driving also supplies the transition, so a
  // drag never inherits the swipe's snap-back easing.
  const dndTransform = CSS.Transform.toString(transform);
  const cardStyle = {
    transform:
      [dndTransform, offset ? `translateX(${offset}px)` : null]
        .filter(Boolean)
        .join(" ") || undefined,
    transition: dndTransform
      ? transition
      : dragging
        ? "none"
        : "transform 160ms ease",
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.85 : undefined,
    WebkitTouchCallout: "none",
  };

  // Left-gutter drag handle, overlaying the card rather than floating outside
  // it — the card stays a single rectangle with one corner radius.
  const dragHandle = isPending && (
    <button
      type="button"
      ref={setActivatorNodeRef}
      aria-label="Drag to reorder"
      className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center z-10 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors"
      style={{ touchAction: "none" }}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical className="w-3.5 h-3.5" />
    </button>
  );

  // Pointer-agnostic edit/clear menu (works with mouse + touch, unlike swipe).
  const actionsMenu = editable ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Session actions"
          className="absolute right-0 top-0 bottom-0 w-8 flex items-center justify-center z-10 text-muted-foreground/50 hover:text-foreground transition-colors"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <MoreVertical className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={() => onEditSession?.(session)}>
          <Pencil className="w-3.5 h-3.5 mr-2" />
          Edit exercises
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={() => onClearSession?.(session)}
        >
          <Trash2 className="w-3.5 h-3.5 mr-2" />
          Clear session
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  // Swipe backdrops. Mounted only mid-swipe, and only rounded on the edge they
  // grow from, so nothing can show through the card's corners at rest.
  const swipeBackdrops = editable ? (
    <>
      {offset > 0 && (
        <div
          className="absolute inset-y-0 left-0 flex items-center justify-start pl-3 gap-1 rounded-l-md bg-cardio text-white text-xs font-medium overflow-hidden whitespace-nowrap pointer-events-none"
          style={{ width: Math.min(offset + 8, maxReveal + 8) }}
          aria-hidden
        >
          <Pencil className="w-4 h-4" /> Edit
        </div>
      )}
      {offset < 0 && (
        <div
          className="absolute inset-y-0 right-0 flex items-center justify-end pr-3 gap-1 rounded-r-md bg-destructive text-destructive-foreground text-xs font-medium overflow-hidden whitespace-nowrap pointer-events-none"
          style={{ width: Math.min(-offset + 8, maxReveal + 8) }}
          aria-hidden
        >
          Clear <Trash2 className="w-4 h-4" />
        </div>
      )}
    </>
  ) : null;

  // touch-action: pan-y keeps vertical scroll native while JS owns the
  // horizontal swipe (without it the browser eats the gesture).
  const swipeProps = editable
    ? { style: { touchAction: "pan-y" }, ...handlers }
    : {};

  let icon;
  let subtitle;
  if (status === "completed") {
    icon = (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-white">
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
    subtitle = [
      name,
      formatSessionDate(completed_at),
      duration_minutes ? `${duration_minutes} min` : null,
      session_rating != null ? `Effort ${session_rating}/10` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  } else if (status === "skipped") {
    icon = (
      <span className="h-5 w-5 rounded-full border-[1.5px] border-muted-foreground/40" />
    );
    subtitle = "Skipped this week";
  } else if (status === "missed") {
    icon = (
      <span className="flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] border-muted-foreground/60 text-muted-foreground/70">
        <X className="h-3 w-3" strokeWidth={3} />
      </span>
    );
    subtitle = "Missed";
  } else if (isAssigned) {
    icon = <span className="h-5 w-5 rounded-full border-2 border-primary" />;
    subtitle = [name || "Built", builder ? `by ${builder}` : "ready", session.target_minutes ? `${session.target_minutes} min` : null]
      .filter(Boolean)
      .join(" · ");
  } else {
    icon = (
      <span className="h-5 w-5 rounded-full border-[1.5px] border-dashed border-muted-foreground/60" />
    );
    subtitle = offered.length
      ? `${offered[0].name.split(" ")[0]} built it · ready to use`
      : session.target_minutes
        ? `${session.target_minutes} min · not built yet`
        : "Not built yet";
  }

  // The day's date leads the subtitle (#92), e.g. "Wed 8 Oct · not built yet".
  if (session.dayLabel) subtitle = [session.dayLabel, subtitle].filter(Boolean).join(" · ");

  return (
    <div
      ref={setNodeRef}
      style={cardStyle}
      className={cn(
        "group relative select-none bg-card",
        isNext && "bg-primary/[0.07]",
      )}
    >
      {swipeBackdrops}
      {dragHandle}
      <button
        type="button"
        onClick={onClickSession}
        {...swipeProps}
        className={cn(
          "flex w-full min-w-0 items-center gap-3 py-3 pl-7 text-left transition-colors hover:bg-muted/30",
          editable ? "pr-10" : "pr-3",
        )}
      >
        {icon}
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate text-sm font-semibold",
              status === "missed" || status === "skipped" ? "text-muted-foreground" : "text-white",
              status === "skipped" && "line-through",
            )}
          >
            {title}
          </span>
          <span className="mt-px block truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
          {didIt.length > 0 && (
            <span className="mt-0.5 flex items-center gap-1 truncate text-xs text-secondary">
              <Check className="h-3 w-3 shrink-0" strokeWidth={3} />
              {`${didIt.join(", ")} did it`}
            </span>
          )}
          {isPending && session.plan_note && (
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {session.plan_note}
            </span>
          )}
          {status === "completed" && session_notes && (
            <span className="mt-0.5 block truncate text-xs italic text-muted-foreground">
              {`“${session_notes}”`}
            </span>
          )}
        </span>
        {isNext && (
          <span className="shrink-0 text-xs font-semibold text-primary">Next</span>
        )}
      </button>
      {actionsMenu}
    </div>
  );
};

export default TimelineSessionItem;
